import { st } from "~/sassmall/i18n";
import { getCurrentDisplayPreference } from "~/utils/localeCurrencyPreference";
import { clearSassmallAuth, setSassmallAuthMode, setSassmallToken } from "../storage";
import {
  getSassmallResponseMessage,
  isSassmallResponseSuccess,
  sassmallRequest,
  type SassmallApiResponse,
} from "./client";

export interface SassmallLoginData {
  token: string;
  user?: Record<string, any>;
  walletAccount?: Record<string, any>;
}

async function currentDisplayPreference() {
  const { useMainStore } = await import("~/composables/store");
  const store = useMainStore();
  const locale = typeof window === "undefined" ? "" : window.localStorage.getItem("i18n_current_locale");
  return getCurrentDisplayPreference(store, locale);
}

export interface SassmallEmailCodeSendData {
  expiresInSeconds?: number;
  nextSendInSeconds?: number;
  sent?: boolean;
  existingCodeValid?: boolean;
  message?: string;
}

export type SassmallOAuthAction =
  | "LOGIN_SUCCESS"
  | "EMAIL_REQUIRED"
  | "EMAIL_CODE_REQUIRED"
  | "EMAIL_OCCUPIED"
  | "PROVIDER_CONFLICT"
  | "ACCOUNT_DISABLED";

export interface SassmallOAuthProvider {
  provider: string;
  displayName: string;
  clientId?: string;
  webClientId?: string;
  appId?: string;
  redirectUri?: string;
  webRedirectUri?: string;
  config?: Record<string, any>;
}

export interface SassmallOAuthAuthData {
  action: SassmallOAuthAction;
  flowToken?: string;
  maskedEmail?: string;
  login?: SassmallLoginData;
}

function applyLoginResponse(response: SassmallApiResponse<SassmallLoginData>) {
  if (!isSassmallResponseSuccess(response) || !response.data?.token) {
    throw new Error(getSassmallResponseMessage(response, st("登录失败")));
  }
  setSassmallToken(response.data.token);
  setSassmallAuthMode("LOCAL");
  return response;
}

export function applySassmallLoginData(data?: SassmallLoginData) {
  if (!data?.token) throw new Error(st("登录失败"));
  setSassmallToken(data.token);
  setSassmallAuthMode("LOCAL");
  return data;
}

export async function loginSassmallEmailPassword(param: { account: string; password: string }) {
  const response = await sassmallRequest<SassmallLoginData>("/portal/auth/login/email-password", {
    method: "POST",
    body: param,
    skipAuth: true,
  });
  return applyLoginResponse(response);
}

export async function loginSassmallEmailCode(param: { email: string; code: string }) {
  const response = await sassmallRequest<SassmallLoginData>("/portal/auth/login/email-code", {
    method: "POST",
    body: param,
    skipAuth: true,
  });
  return applyLoginResponse(response);
}

export async function sendSassmallEmailCode(email: string, scene: "REGISTER" | "LOGIN" | "PROFILE_UPDATE") {
  const preference = scene === "REGISTER" ? await currentDisplayPreference() : null;
  return sassmallRequest<SassmallEmailCodeSendData>("/portal/auth/email-code/send", {
    method: "POST",
    body: { email, scene, ...(preference ? { interfaceLanguage: preference.effectiveLanguage } : {}) },
    skipAuth: true,
  });
}

export async function registerSassmallEmail(param: {
  email: string;
  password: string;
  code: string;
  agreementVersion?: string;
  inviteCode?: string;
}) {
  const response = await sassmallRequest<SassmallLoginData>("/portal/auth/register/email", {
    method: "POST",
    body: {
      ...param,
      ...await currentDisplayPreference(),
      registerPort: "PC",
      agreementVersion: param.agreementVersion || "CURRENT",
    },
    skipAuth: true,
  });
  return applyLoginResponse(response);
}

export function querySassmallOAuthProviders(clientType = "PC") {
  return sassmallRequest<SassmallOAuthProvider[]>("/portal/auth/oauth/providers", {
    method: "GET",
    query: { clientType },
    skipAuth: true,
  });
}

export function exchangeSassmallOAuth(payload: {
  provider: string;
  authorizationCode?: string;
  idToken?: string;
  redirectUri?: string;
  clientType: "PC" | "H5";
}) {
  return exchangeSassmallOAuthWithPreference(payload);
}

async function exchangeSassmallOAuthWithPreference(payload: Parameters<typeof exchangeSassmallOAuth>[0]) {
  return sassmallRequest<SassmallOAuthAuthData>("/portal/auth/oauth/exchange", {
    method: "POST",
    body: { ...payload, ...await currentDisplayPreference() },
    skipAuth: true,
  });
}

export function sendSassmallOAuthEmailCode(payload: { flowToken: string; email?: string }) {
  return sassmallRequest<SassmallEmailCodeSendData>("/portal/auth/oauth/email-code/send", {
    method: "POST",
    body: payload,
    skipAuth: true,
  });
}

export async function completeSassmallOAuth(payload: {
  flowToken: string;
  email?: string;
  code?: string;
  agreementVersion?: string;
}) {
  return sassmallRequest<SassmallOAuthAuthData>("/portal/auth/oauth/complete", {
    method: "POST",
    body: { ...payload, ...await currentDisplayPreference() },
    skipAuth: true,
  });
}

export function querySassmallCurrentUser() {
  return sassmallRequest<Record<string, any>>("/portal/auth/me", { method: "GET" });
}

export function changeSassmallPassword(param: { currentPassword: string; newPassword: string }) {
  return sassmallRequest<null>("/portal/auth/password/change", {
    method: "POST",
    body: param,
  });
}

export function logoutSassmall() {
  clearSassmallAuth();
}
